import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { MsgAction } from '../store/actions';
import { isRealtimeUsable } from '../config/socket/socket';

/**
 * The "N missed calls" badge on the nav rail.
 *
 * The server has always had both halves of this — `missedCall:get` returns the count
 * and `missedCall:read` zeroes it (`helpers.markMissedCallAsRead`) — but the web client
 * only ever emitted the first. So the badge lit on the first missed call and stayed lit
 * for the life of the account: opening the call list, the missed tab, or the call itself
 * changed nothing, because nothing told the server the list had been seen.
 *
 * Both emits are guarded on the connection being usable. A socket rejected at the
 * handshake has `active: false` with no retry scheduled, and `emit` on it neither throws
 * nor reports — the packet lands in a buffer that is never flushed, so the ack never
 * arrives. Same guard the message read path uses.
 *
 * Neither emit carries a `user_id`: the server reads the member from `socket.user.id`,
 * and a client-supplied id is ignored — sending one only implies it is honoured.
 */
export const useMissedCallBadge = (socket) => {
  const dispatch = useDispatch();

  /** Re-read the count from the server, which is the only authority on it. */
  const refresh = useCallback(() => {
    if (!isRealtimeUsable(socket)) return;
    socket.emit('missedCall:get', {}, (data) => {
      const next = Number(data?.unread_missed_calls);
      dispatch(MsgAction.missedCallCount(Number.isFinite(next) && next > 0 ? next : 0));
    });
  }, [socket, dispatch]);

  /**
   * Mark every missed call read.
   *
   * The local count follows the ACK rather than the emit. `missedCall:read` fails
   * silently on the server (it logs and returns without acking), and clearing the badge
   * on a write that did not land would hide the missed calls until the next reload
   * brought them back.
   */
  const markRead = useCallback(() => {
    if (!isRealtimeUsable(socket)) return;
    socket.emit('missedCall:read', {}, () => dispatch(MsgAction.missedCallCount(0)));
  }, [socket, dispatch]);

  return { refresh, markRead };
};

export default useMissedCallBadge;
